import * as WebPushAPI from 'api/WebPushAPI';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { call, put, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { getErrorResponse } from 'utils/xhr';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import { CreateWebPushFormValues, DeleteWebPushFormValues, TestWebPushFormValues } from './types';

/**
 * fetchWebPushSaga
 * Saga for fetching web push data
 */
export function* fetchWebPushSaga() {
  try {
    const { results } = yield call(WebPushAPI.getWebPush);
    yield put(actions.fetchWebPushResponse(results));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.fetchWebPushError(err));
    if ((error as any).response?.status === 403) return;
    const message = `Please try again later. If the problem persists, contact your CSM.`;
    yield put(addNegativeToast(message));
  }
}

/**
 * createWebPushSaga
 * Saga for creating web push integration
 */
export function* createWebPushSaga({ params }: { params: CreateWebPushFormValues }) {
  try {
    yield call(WebPushAPI.createWebPush, params);
    yield put(actions.createWebPushResponse(params));
    yield put(addPositiveToast('Successfully created web push integration.'));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.createWebPushError(err));
    const message = `Please try again later. If the problem persists, contact your CSM.`;
    yield put(addNegativeToast(message));
  }
}

/**
 * deleteWebPushSaga
 * Saga for updating web push integration
 */
export function* deleteWebPushSaga({ params }: { params: DeleteWebPushFormValues }) {
  try {
    yield call(WebPushAPI.deleteWebPush, params);
    yield put(actions.deleteWebPushResponse(params));
    yield put(addPositiveToast('Successfully deleted web push integration.'));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.deleteWebPushError(err));
    const message = `Please try again later. If the problem persists, contact your CSM.`;
    yield put(addNegativeToast(message));
  }
}

/**
 * sendWebPushSaga
 * Saga for updating web push integration
 */
export function* sendWebPushSaga({ params }: { params: TestWebPushFormValues }) {
  try {
    yield call(WebPushAPI.sendWebPush, params);
    yield put(actions.sendWebPushResponse(params));
    yield put(addPositiveToast('Successfully sent web push notification.'));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.sendWebPushError(err));
    const message = `Please try again later. If the problem persists, contact your CSM.`;
    yield put(addNegativeToast(message));
  }
}

addToRootSaga([
  takeLatest<{ type: string }>(
    [
      actionTypes.FETCH_WEB_PUSH_REQUEST,
      actionTypes.CREATE_WEB_PUSH_RESPONSE,
      actionTypes.DELETE_WEB_PUSH_RESPONSE,
    ],
    fetchWebPushSaga,
  ),
  takeLatest<{ type: string; params: CreateWebPushFormValues }>(
    actionTypes.CREATE_WEB_PUSH_REQUEST,
    createWebPushSaga,
  ),
  takeLatest<{ type: string; params: DeleteWebPushFormValues }>(
    actionTypes.DELETE_WEB_PUSH_REQUEST,
    deleteWebPushSaga,
  ),
  takeLatest<{ type: string; params: TestWebPushFormValues }>(
    actionTypes.SEND_WEB_PUSH_REQUEST,
    sendWebPushSaga,
  ),
]);
